import { Context } from "@/util-components/root/context";
import { useContext, useMemo } from "react";

/**
 * State object representing the user's subscription status.
 */
export interface SubscriptionState {
  /** The raw subscription status string from the backend (e.g., 'active', 'trialing', 'past_due'). */
  status: string | undefined;
  /** The raw subscription plan type (e.g., 'basic', 'pro', 'custom'). */
  type: string | undefined;
  /** The date until which the user has access. */
  accessUntil: Date | undefined;
  
  // Helper flags
  /** True if the subscription is currently in a trial period. */
  isTrialing: boolean;
  /** True if the subscription is past due (payment failed but not yet canceled). */
  isOverdue: boolean; // past_due
  /** True if the user has active access (status is 'active', 'trialing', or 'grace_period'). */
  isActive: boolean; // active or trialing
  /** True if the subscription has been canceled. */
  isCanceled: boolean;
  /** True if the subscription is expired or inactive. */
  isExpired: boolean;
  
  // Plan checks
  /** True if the user is on the 'pro' plan. */
  isPro: boolean;
  /** True if the user is on the 'basic' plan. */
  isBasic: boolean;
  /** True if the user has a custom plan. */
  isCustom: boolean;
  /** True if the organization has already used their trial period. */
  hasUsedTrial: boolean;
  /** True if the organization has a stripe customer id. */
  hasStripeId: boolean;
}

/**
 * Hook to access the current user's subscription status and plan details.
 * 
 * This hook derives useful boolean flags from the raw subscription data in the user context,
 * making it easier to check for access rights, trial status, or specific plan features.
 * 
 * @returns {SubscriptionState} An object containing subscription details and helper flags.
 * 
 * @example
 * const { isActive, isPro } = useSubscriptions();
 * 
 * if (!isActive) {
 *   return <Redirect to="/pricing" />;
 * }
 * 
 * if (isPro) {
 *   return <ProFeature />;
 * }
 */
export const useSubscriptions = (): SubscriptionState => {
  const { user } = useContext(Context);

  return useMemo(() => {
    const status = user?.subscriptionStatus;
    const type = user?.subscriptionType;
    const accessUntilStr = user?.accessUntil;
    const accessUntil = accessUntilStr ? new Date(accessUntilStr) : undefined;
    const hasUsedTrial = user?.hasUsedTrial || false;
    const hasStripeId = user?.hasStripeId || false;

    return {
      status,
      type,
      accessUntil,
      hasUsedTrial,
      hasStripeId,
      
      isTrialing: status === "trialing",
      isOverdue: status === "past_due" || status === "grace_period",
      isActive: status === "active" || status === "trialing" || status === "grace_period",
      isCanceled: status === "canceled",
      isExpired: status === "expired" || status === "inactive",
      
      isPro: type === "pro",
      isBasic: type === "basic",
      isCustom: type === "custom",
    };
  }, [user]);
};
